import { useState } from 'react';
import { OnboardingStep } from './OnboardingStep';
import { CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

interface PlanSelectionStepProps {
  onNext: (plan: string) => void;
  onBack: () => void;
  dealVolume: string;
}

const PLANS = [
  { id: 'free', name: 'Free', price: '0', volume: '1-5', features: ['Basic CRM', 'Up to 500 Leads', 'Manual Tracking'] },
  { id: 'solo', name: 'Solo', price: '29', volume: '6-20', features: ['Everything in Free', 'Basic Automations', 'E-Signatures', 'Up to 2,500 Leads'] },
  { id: 'pro', name: 'Pro', price: '79', volume: '21-50', features: ['Everything in Solo', 'Advanced Automations', 'Unlimited Leads', 'Property Management'] },
  { id: 'team', name: 'Team', price: '199', volume: '50+', features: ['Everything in Pro', 'Up to 5 Users', 'Team Leaderboards', 'Priority Support'] }
];

export function PlanSelectionStep({ onNext, onBack, dealVolume }: PlanSelectionStepProps) {
  // Recommend plan based on deal volume
  const recommendedPlanId = PLANS.find(p => p.volume === dealVolume)?.id || 'solo';
  const [selectedPlan, setSelectedPlan] = useState(recommendedPlanId);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleComplete = async () => {
    if (selectedPlan === 'free') {
      onNext(selectedPlan);
    } else {
      setIsProcessing(true);
      toast.loading("Redirecting to secure checkout...");
      try {
        const { data, error } = await supabase.functions.invoke('stripe-checkout', {
          body: {
            plan: selectedPlan,
            success_url: `${window.location.origin}/dashboard?onboarding_success=true`,
            cancel_url: `${window.location.origin}/onboarding`
          }
        });

        if (error) throw error;

        if (data?.url) {
           onNext(selectedPlan);
           setTimeout(() => {
             window.location.href = data.url;
           }, 1500);
        } else {
           throw new Error("No checkout URL returned.");
        }
      } catch (err: any) {
        toast.dismiss();
        toast.error(`Checkout failed: ${err.message}`);
        setIsProcessing(false);
      }
    }
  };

  return (
    <OnboardingStep
      title="Choose your plan"
      subtitle="Based on your deal volume, we've highlighted our recommendation."
      onNext={handleComplete}
      onBack={onBack}
      nextLabel={selectedPlan === 'free' ? "Start for Free" : "Proceed to Checkout"}
      isNextDisabled={isProcessing}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PLANS.map(plan => {
          const isRecommended = plan.id === recommendedPlanId;
          const isSelected = selectedPlan === plan.id;

          return (
            <div
              key={plan.id}
              onClick={() => !isProcessing && setSelectedPlan(plan.id)}
              className={`relative p-6 rounded-2xl border-2 transition-all cursor-pointer flex flex-col h-full ${
                isSelected
                  ? 'border-[var(--t-primary)] bg-[var(--t-primary-dim)]'
                  : 'border-[var(--t-border-subtle)] bg-[var(--t-background)] hover:border-[var(--t-border-hover)]'
              }`}
            >
              {isRecommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[var(--t-success)] text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-md">
                  Recommended
                </div>
              )}

              <div className="flex justify-between items-start mb-4">
                <h3 className={`text-xl font-bold ${isSelected ? 'text-white' : 'text-[var(--t-text)]'}`}>{plan.name}</h3>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-[var(--t-primary)] bg-[var(--t-primary)] text-white' : 'border-[var(--t-text-muted)] transparent'}`}>
                  {isSelected && <CheckCircle2 size={12} strokeWidth={4} />}
                </div>
              </div>

              <div className="mb-6">
                <span className={`text-3xl font-black ${isSelected ? 'text-[var(--t-primary)]' : 'text-white'}`}>${plan.price}</span>
                <span className="text-[var(--t-text-muted)] text-sm">/mo</span>
              </div>

              <ul className="space-y-2 mt-auto">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 size={16} className={`shrink-0 mt-0.5 ${isSelected ? 'text-[var(--t-primary)]' : 'text-[var(--t-text-muted)]'}`} />
                    <span className={isSelected ? 'text-white' : 'text-[var(--t-text-secondary)]'}>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </OnboardingStep>
  );
}
